import { styled } from "@linaria/react";
import React from "react";
import classNames from "classnames";
import ReactPanZoom from "../PanZoomBox";
import { color } from "..";
import t from "../i18n";
import { AddLine, SubtractFill } from "../IconV2/react";
import TooltipV2 from "../TooltipV2";
import OverlayWrapper from "../TooltipV2/OverlayWrapper";

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 400px;
  border: 1px solid;
  border-color: ${color.titanium100};
  border-radius: 8px;
  width: 100%;
  background-color: ${color.hydrogen};
`;

const Plus = styled(AddLine)`
  height: 35px;
  width: 35px;
  padding: 8px;
  border: 1px solid;
  border-color: ${color.titanium100};
  background-color: ${color.hydrogen};
  border-radius: 8px 8px 0 0;
  color: ${color.carbon800};
  :hover {
    cursor: pointer;
    color: ${color.carbon900};
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    :hover {
      color: ${color.titanium800};
    }
  }
  display: none;
`;

const ButtonZoomIn = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const Minus = styled(SubtractFill)`
  height: 35px;
  width: 35px;
  padding: 8px;
  border: 1px solid;
  border-color: ${color.titanium100};
  background-color: ${color.hydrogen};
  border-top: none;
  border-radius: 0 0 8px 8px;
  color: ${color.carbon800};
  :hover {
    cursor: pointer;
    color: ${color.carbon900};
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    :hover {
      color: ${color.titanium800};
    }
  }
  display: none;
`;

const ButtonZoomOut = styled.div`
  position: absolute;
  top: 54px;
  right: 20px;
`;

const StyledTooltip = styled(TooltipV2)`
  &.disabled {
    display: none;
  }
`;

const SynopticWrapper = styled.div`
  overflow: hidden;
  flex-grow: 6;
  border-radius: 8px;
  & img {
    margin: auto;
    display: block;
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
  position: relative;
  &:hover ${Plus} {
    display: block;
  }
  &:hover ${Minus} {
    display: block;
  }
`;

const ModelSynopticImage = styled.img`
  background-color: ${color.hydrogen};
`;

interface P {
  data: string;
  loadingOverlay?: React.ReactNode;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.5;

const ImageReader = ({ data, loadingOverlay }: P) => {
  const [zoom, setZoom] = React.useState(1);
  const [dx, setDx] = React.useState(0);
  const [dy, setDy] = React.useState(0);
  const synoptic = React.useRef<HTMLImageElement>(null);

  const handleZoomIn = () => {
    if (zoom < ZOOM_MAX) {
      setZoom(zoom + ZOOM_STEP);
    }
  };

  const handleZoomOut = () => {
    if (zoom > ZOOM_MIN) {
      setZoom(zoom - ZOOM_STEP);
    }
  };

  const handleCenter = () => {
    // reset synoptic position
    setDx(-1);
    setDy(-1);
    setZoom(1);
  };

  const onPan = () => {
    // reset Dx and Dy
    setDx(0);
    setDy(0);
  };

  return (
    <Wrapper>
      <SynopticWrapper onDoubleClick={handleCenter}>
        <ReactPanZoom zoom={zoom} pandx={dx} pandy={dy} onPan={onPan} width="100%" height="100%">
          {loadingOverlay}
          <ModelSynopticImage
            ref={synoptic}
            src={`data:image/svg+xml;base64,${data}`}
            alt="Synoptic"
          />
        </ReactPanZoom>

        <StyledTooltip
          className={classNames({ disabled: zoom === ZOOM_MAX })}
          placement="right"
          overlay={<OverlayWrapper text={t("image_reader.zoom_in")} />}
        >
          <ButtonZoomIn>
            <div onDoubleClick={e => e.stopPropagation()}>
              <Plus
                onClick={handleZoomIn}
                className={classNames({ disabled: zoom === ZOOM_MAX })}
              />
            </div>
          </ButtonZoomIn>
        </StyledTooltip>

        <StyledTooltip
          placement="right"
          className={classNames({ disabled: zoom === ZOOM_MIN })}
          overlay={t("image_reader.zoom_out")}
        >
          <ButtonZoomOut>
            <div onDoubleClick={e => e.stopPropagation()}>
              <Minus
                onClick={handleZoomOut}
                className={classNames({ disabled: zoom === ZOOM_MIN })}
              />
            </div>
          </ButtonZoomOut>
        </StyledTooltip>
      </SynopticWrapper>
    </Wrapper>
  );
};

export default React.memo(ImageReader);
